import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { workspaceApi } from "./api";
import { api } from "@/lib/api";

const EMPTY_ARRAY = [];

/**
 * /workspace/debug — SONMI's cognitive DevTools.
 *
 * This is not a product surface. It is a research surface. Its job is
 * to make how SONMI thinks inspectable while the architecture is still
 * small enough to change.
 *
 * Four synchronized panes:
 *   Workspace  — what you see (artifacts, focus)
 *   Attention  — what is active (focused artifact, working set, dormant)
 *   Reasoning  — facts, inferences, candidate/rejected/activated relationships
 *   Language   — what SONMI actually says (narration)
 *
 * A timeline scrubber at the bottom drives the state of all four panes.
 * Every observation is clickable; clicking reveals its structured
 * provenance (evidence list, memory source, scores, lifecycle).
 */

export default function WorkspaceDebugPage() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // index into understandings timeline
  const [selectedProvenance, setSelectedProvenance] = useState(null);
  const [error, setError] = useState(null);

  // Load session list
  useEffect(() => {
    workspaceApi
      .listSessions(true)
      .then((list) => {
        setSessions(list);
        if (list.length && !sessionId) setSessionId(list[0].id);
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load trace for selected session
  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    api
      .get(`/workspace/sessions/${sessionId}/trace`)
      .then((r) => {
        setTrace(r.data);
        // Start at the latest understanding
        const n = (r.data.understandings || []).length;
        setStep(Math.max(0, n - 1));
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const refreshTrace = useCallback(async () => {
    if (!sessionId) return;
    // Force a new understanding to be recorded, then reload trace
    try {
      await workspaceApi.getUnderstanding(sessionId);
    } catch (_) {}
    const r = await api.get(`/workspace/sessions/${sessionId}/trace`);
    setTrace(r.data);
    const n = (r.data.understandings || []).length;
    setStep(Math.max(0, n - 1));
  }, [sessionId]);

  const understandings = trace?.understandings || [];
  const events = trace?.events || EMPTY_ARRAY;
  const artifacts = trace?.artifacts || EMPTY_ARRAY;
  const currentUnderstanding = understandings[step] || null;

  // Reconstruct the workspace state as it was at the time of the
  // currently-selected understanding: walk events forward from the start
  // and take the last focus event that occurred at or before this
  // understanding's generated_at.
  const stateAtStep = useMemo(() => {
    if (!currentUnderstanding) {
      return { focusId: null, openArtifactIds: artifacts.map((a) => a.id) };
    }
    const cutoff = currentUnderstanding.generated_at;
    let focusId = null;
    const openSet = new Set();
    for (const e of events) {
      if (e.occurred_at > cutoff) break;
      if (e.type === "artifact_opened" && e.artifact_id) openSet.add(e.artifact_id);
      if (e.type === "artifact_closed" && e.artifact_id) openSet.delete(e.artifact_id);
      if (e.type === "artifact_focused") focusId = e.artifact_id || null;
    }
    return {
      focusId,
      openArtifactIds: Array.from(openSet),
    };
  }, [currentUnderstanding, events, artifacts]);

  const artifactsById = useMemo(
    () => Object.fromEntries(artifacts.map((a) => [a.id, a])),
    [artifacts]
  );

  return (
    <div
      data-testid="workspace-debug"
      style={{
        height: "100vh",
        background: "#050508",
        color: "rgba(255,255,255,0.88)",
        fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
        fontSize: 12,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Header
        sessions={sessions}
        sessionId={sessionId}
        onSelectSession={setSessionId}
        onRefresh={refreshTrace}
        loading={loading}
      />

      {error && (
        <div style={{ padding: 12, color: "rgba(255,180,180,0.85)" }}>{error}</div>
      )}

      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: 1,
          background: "rgba(255,255,255,0.06)",
          minHeight: 0,
        }}
      >
        <Pane title="Workspace" testid="pane-workspace">
          <WorkspacePane
            artifacts={artifacts}
            focusId={stateAtStep.focusId}
            openIds={stateAtStep.openArtifactIds}
          />
        </Pane>
        <Pane title="Attention" testid="pane-attention">
          <AttentionPane
            understanding={currentUnderstanding}
            artifactsById={artifactsById}
            focusId={stateAtStep.focusId}
          />
        </Pane>
        <Pane title="Reasoning" testid="pane-reasoning">
          <ReasoningPane
            understanding={currentUnderstanding}
            artifactsById={artifactsById}
            onSelectProvenance={setSelectedProvenance}
          />
        </Pane>
        <Pane title="Language" testid="pane-language">
          <LanguagePane understanding={currentUnderstanding} />
        </Pane>
      </div>

      <TimelineBar
        understandings={understandings}
        events={events}
        step={step}
        onStep={setStep}
      />

      {selectedProvenance && (
        <ProvenanceOverlay
          detail={selectedProvenance}
          artifactsById={artifactsById}
          onClose={() => setSelectedProvenance(null)}
        />
      )}
    </div>
  );
}

/* ---------- header ---------- */
function Header({ sessions, sessionId, onSelectSession, onRefresh, loading }) {
  return (
    <div
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: "#08080d",
      }}
    >
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.6 }}>
        SONMI · debug
      </div>
      <select
        data-testid="debug-session-picker"
        value={sessionId || ""}
        onChange={(e) => onSelectSession(e.target.value)}
        style={{
          background: "rgba(255,255,255,0.06)",
          color: "inherit",
          border: "1px solid rgba(255,255,255,0.14)",
          padding: "6px 10px",
          borderRadius: 4,
          fontSize: 12,
          fontFamily: "inherit",
          flex: 1,
          maxWidth: 480,
        }}
      >
        {sessions.map((s) => (
          <option key={s.id} value={s.id} style={{ background: "#0a0a10" }}>
            {s.title} · {new Date(s.last_active_at).toLocaleString()}
            {s.archived ? " (archived)" : ""}
          </option>
        ))}
      </select>
      <button
        data-testid="debug-refresh"
        onClick={onRefresh}
        disabled={loading}
        style={{
          background: "rgba(255,255,255,0.06)",
          color: "inherit",
          border: "1px solid rgba(255,255,255,0.14)",
          padding: "6px 12px",
          borderRadius: 4,
          fontSize: 11,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          cursor: loading ? "wait" : "pointer",
          fontFamily: "inherit",
        }}
      >
        {loading ? "…" : "observe"}
      </button>
      <a
        href="/workspace"
        style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, textDecoration: "none" }}
      >
        ← workspace
      </a>
    </div>
  );
}

function Pane({ title, testid, children }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "#050508",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <div
        style={{
          padding: "6px 12px",
          fontSize: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.48)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(255,255,255,0.02)",
        }}
      >
        {title}
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: 12, minHeight: 0 }}>{children}</div>
    </div>
  );
}

/* ---------- pane 1: workspace ---------- */
function WorkspacePane({ artifacts, focusId, openIds }) {
  const open = artifacts.filter((a) => openIds.includes(a.id));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {open.length === 0 && <Empty>no artifacts</Empty>}
      {open.map((a) => {
        const isFocused = a.id === focusId;
        return (
          <div
            key={a.id}
            style={{
              padding: "8px 10px",
              border: `1px solid ${isFocused ? "rgba(255,240,140,0.5)" : "rgba(255,255,255,0.1)"}`,
              background: isFocused ? "rgba(255,240,140,0.05)" : "transparent",
              borderRadius: 4,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.9)" }}>
                {a.title}{" "}
                <span style={{ opacity: 0.4, fontSize: 10 }}>· {a.type}</span>
              </div>
              {isFocused && (
                <span style={{ color: "rgba(255,240,140,0.9)", fontSize: 9, letterSpacing: "0.14em" }}>
                  FOCUSED
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "rgba(255,255,255,0.42)",
                marginTop: 4,
                maxHeight: 60,
                overflow: "hidden",
                whiteSpace: "pre-wrap",
              }}
            >
              {(a.content || "").slice(0, 240)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- pane 2: attention ---------- */
function AttentionPane({ understanding, artifactsById, focusId }) {
  const activated = understanding?.activated_artifact_ids || [];
  const dormant = understanding?.dormant_artifact_ids || [];
  const focused = focusId ? artifactsById[focusId] : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Section label="focus (light source)">
        {focused ? (
          <Chip color="rgba(255,240,140,0.9)" strong>{focused.title}</Chip>
        ) : (
          <Empty>no focus</Empty>
        )}
      </Section>
      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.32)", textAlign: "center" }}>↓ activates ↓</div>
      <Section label={`activated working set (${activated.length})`}>
        {activated.length === 0 && <Empty>none — focus reaches for nothing</Empty>}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {activated.map((id) => (
            <Chip key={id} color="rgba(150,240,180,0.8)">
              {artifactsById[id]?.title || id.slice(0, 8)}
            </Chip>
          ))}
        </div>
      </Section>
      <Section label={`dormant (${dormant.length})`}>
        {dormant.length === 0 && <Empty>none marked dormant</Empty>}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {dormant.map((id) => (
            <Chip key={id} color="rgba(255,255,255,0.35)">
              {artifactsById[id]?.title || id.slice(0, 8)}
            </Chip>
          ))}
        </div>
      </Section>
    </div>
  );
}

/* ---------- pane 3: reasoning ---------- */
function ReasoningPane({ understanding, artifactsById, onSelectProvenance }) {
  if (!understanding) return <Empty>no understanding yet</Empty>;
  const activated = understanding.relationships || [];
  const rejected = understanding.rejected_relationships || [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Section label={`facts (${understanding.facts?.length || 0})`}>
        {(understanding.facts || []).map((f, i) => (
          <Obs key={i} obs={f} kind="fact" onClick={() => onSelectProvenance({ kind: "fact", data: f })} />
        ))}
      </Section>
      <Section label={`inferences (${understanding.inferences?.length || 0})`}>
        {(understanding.inferences || []).map((f, i) => (
          <Obs key={i} obs={f} kind="inference" onClick={() => onSelectProvenance({ kind: "inference", data: f })} />
        ))}
      </Section>
      <Section label={`activated relationships (${activated.length})`}>
        {activated.length === 0 && <Empty>none</Empty>}
        {activated.map((r, i) => (
          <Rel
            key={i}
            r={r}
            state="activated"
            artifactsById={artifactsById}
            onClick={() => onSelectProvenance({ kind: "relationship", data: r })}
          />
        ))}
      </Section>
      <Section label={`rejected relationships (${rejected.length})`}>
        {rejected.length === 0 && <Empty>none considered</Empty>}
        {rejected.map((r, i) => (
          <Rel
            key={i}
            r={r}
            state="rejected"
            artifactsById={artifactsById}
            onClick={() => onSelectProvenance({ kind: "relationship", data: r })}
          />
        ))}
      </Section>
    </div>
  );
}

function Obs({ obs, kind, onClick }) {
  const color = kind === "fact" ? "rgba(150,240,180,0.7)" : "rgba(200,180,255,0.7)";
  return (
    <div
      onClick={onClick}
      style={{
        padding: "6px 8px",
        borderLeft: `2px solid ${color}`,
        background: "rgba(255,255,255,0.02)",
        cursor: "pointer",
        marginBottom: 6,
      }}
    >
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", lineHeight: 1.4 }}>{obs.text}</div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.42)", marginTop: 4, letterSpacing: "0.05em" }}>
        ev {obs.evidence_score?.toFixed(2)} · rel {obs.relevance_score?.toFixed(2)} · {obs.memory_source}
      </div>
    </div>
  );
}

function Rel({ r, state, artifactsById, onClick }) {
  const a = artifactsById[r.a_artifact_id]?.title || r.a_artifact_id.slice(0, 6);
  const b = artifactsById[r.b_artifact_id]?.title || r.b_artifact_id.slice(0, 6);
  const color = state === "activated" ? "rgba(150,240,180,0.7)" : "rgba(255,140,140,0.55)";
  return (
    <div
      onClick={onClick}
      style={{
        padding: "6px 8px",
        borderLeft: `2px solid ${color}`,
        background: "rgba(255,255,255,0.02)",
        cursor: "pointer",
        marginBottom: 6,
        opacity: state === "rejected" ? 0.75 : 1,
      }}
    >
      <div style={{ fontSize: 11 }}>
        <strong>{a}</strong> ↔ <strong>{b}</strong>{" "}
        <span style={{ opacity: 0.5 }}>· {r.kind}</span>
      </div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.42)", marginTop: 4 }}>
        ev {r.evidence_score?.toFixed(2)} · rel {r.relevance_score?.toFixed(2)} · {r.memory_source} ·{" "}
        <span style={{ color: state === "rejected" ? "rgba(255,140,140,0.85)" : "rgba(150,240,180,0.85)" }}>
          {r.lifecycle}
        </span>
      </div>
      {r.activation_reason && (
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", marginTop: 4, fontStyle: "italic" }}>
          "{r.activation_reason}"
        </div>
      )}
    </div>
  );
}

/* ---------- pane 4: language ---------- */
function LanguagePane({ understanding }) {
  if (!understanding) return <Empty>no narration yet</Empty>;
  return (
    <div>
      <div
        style={{
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: 13,
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.85)",
          fontStyle: "italic",
          whiteSpace: "pre-wrap",
        }}
      >
        {understanding.narration || "(silent)"}
      </div>
      <div
        style={{
          fontSize: 9,
          color: "rgba(255,255,255,0.35)",
          marginTop: 16,
          letterSpacing: "0.06em",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          paddingTop: 8,
        }}
      >
        interpreter: {understanding.interpreter} · generated{" "}
        {new Date(understanding.generated_at).toLocaleString()}
      </div>
    </div>
  );
}

/* ---------- timeline ---------- */
function TimelineBar({ understandings, events, step, onStep }) {
  const n = understandings.length;
  return (
    <div
      style={{
        borderTop: "1px solid rgba(255,255,255,0.1)",
        padding: "10px 16px 14px",
        background: "#08080d",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.6 }}>
          timeline · {n} understanding{n === 1 ? "" : "s"} · {events.length} event
          {events.length === 1 ? "" : "s"}
        </div>
        <div style={{ fontSize: 10, opacity: 0.6 }}>
          {understandings[step] && new Date(understandings[step].generated_at).toLocaleTimeString()}
        </div>
      </div>
      {n === 0 ? (
        <Empty>no understandings recorded yet</Empty>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            data-testid="debug-step-back"
            onClick={() => onStep(Math.max(0, step - 1))}
            disabled={step === 0}
            style={buttonMini}
          >
            ←
          </button>
          <input
            data-testid="debug-timeline-scrubber"
            type="range"
            min={0}
            max={Math.max(0, n - 1)}
            value={step}
            onChange={(e) => onStep(parseInt(e.target.value, 10))}
            style={{ flex: 1 }}
          />
          <button
            data-testid="debug-step-forward"
            onClick={() => onStep(Math.min(n - 1, step + 1))}
            disabled={step >= n - 1}
            style={buttonMini}
          >
            →
          </button>
          <span style={{ fontSize: 10, opacity: 0.55, width: 60, textAlign: "right" }}>
            {step + 1} / {n}
          </span>
        </div>
      )}
    </div>
  );
}

/* ---------- provenance overlay ---------- */
function ProvenanceOverlay({ detail, artifactsById, onClose }) {
  const d = detail.data;
  return (
    <div
      data-testid="debug-provenance-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0c0c14",
          border: "1px solid rgba(255,255,255,0.14)",
          borderRadius: 6,
          padding: 20,
          width: "min(680px, 92vw)",
          maxHeight: "82vh",
          overflowY: "auto",
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.55, marginBottom: 12 }}>
          Provenance · {detail.kind}
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, lineHeight: 1.5, marginBottom: 16, color: "rgba(255,255,255,0.92)" }}>
          {detail.kind === "relationship"
            ? `${artifactsById[d.a_artifact_id]?.title || d.a_artifact_id} ↔ ${artifactsById[d.b_artifact_id]?.title || d.b_artifact_id}`
            : d.text}
        </div>

        <ProvRow label="lifecycle" value={d.lifecycle} />
        <ProvRow label="memory source" value={d.memory_source} />
        <ProvRow label="evidence score" value={(d.evidence_score ?? 0).toFixed(2)} />
        <ProvRow label="relevance score" value={(d.relevance_score ?? 0).toFixed(2)} />
        {d.activation_reason && <ProvRow label="activation reason" value={d.activation_reason} />}
        {d.kind === "relationship" && <ProvRow label="kind" value={d.kind} />}

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.55, marginBottom: 6 }}>
            evidence
          </div>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {(d.evidence_list && d.evidence_list.length > 0
              ? d.evidence_list
              : d.evidence && Array.isArray(d.evidence)
              ? d.evidence
              : d.evidence
              ? [d.evidence]
              : []
            ).map((e, i) => (
              <li key={i} style={{ fontSize: 12, lineHeight: 1.5, color: "rgba(255,255,255,0.78)", marginBottom: 4 }}>
                {e}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ marginTop: 20, textAlign: "right" }}>
          <button data-testid="debug-provenance-close" onClick={onClose} style={buttonMini}>
            close
          </button>
        </div>
      </div>
    </div>
  );
}

function ProvRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 12, fontSize: 12, padding: "4px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ width: 140, opacity: 0.5, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ flex: 1, color: "rgba(255,255,255,0.88)" }}>{value}</div>
    </div>
  );
}

/* ---------- shared ---------- */
function Section({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.42)", marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Chip({ color, strong, children }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 8px",
        border: `1px solid ${color}`,
        borderRadius: 12,
        fontSize: 10,
        color: color,
        background: strong ? `${color.replace(/[\d.]+\)$/, "0.1)")}` : "transparent",
      }}
    >
      {children}
    </span>
  );
}

function Empty({ children }) {
  return <div style={{ fontSize: 10, opacity: 0.4, fontStyle: "italic" }}>{children}</div>;
}

const buttonMini = {
  background: "rgba(255,255,255,0.06)",
  color: "rgba(255,255,255,0.75)",
  border: "1px solid rgba(255,255,255,0.14)",
  padding: "4px 10px",
  borderRadius: 3,
  fontSize: 11,
  cursor: "pointer",
  fontFamily: "inherit",
};
